import React, { useState } from 'react';
import { useSprings, animated } from 'react-spring/web.cjs';
import { useDrag } from 'react-use-gesture';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { translations } from 'i18n';
import theme, { imageBorderRadius } from 'themes';

import ImageContainer from 'components/ImageContainer';

const IMAGE_HEIGHT = 160;
const EDGE_DISTANCE = 40;

const SliderContainer = styled.div`
  ${theme.Swiper};
  img {
    border-radius: ${imageBorderRadius};
  }
`;

const StyledSlider = styled.div`
  position: relative;
  height: ${IMAGE_HEIGHT}px;
`;

const ImageContainerStyle = `
  display: inline-block;
  overflow: hidden;
`;

const SingleImageContainerStyle = styled.div`
  margin: auto;
  text-align: center;
  ${theme.Swiper};
`;

const SlideImageStyle = `
  user-drag: none;
`;

const ItemContainer = styled(animated.div)`
  position: absolute;
  will-change: transform;
`;

const SwiperPanel = ({ banners = [] }) => {
  const {
    component: {
      AppDetails: { bannerAltText }
    }
  } = translations;

  const bannerWidth =
    banners.length > 0
      ? (IMAGE_HEIGHT / banners[0].height) * banners[0].width
      : 0;
  const itemWidth = bannerWidth + EDGE_DISTANCE;
  const [active, setActive] = useState(0);
  const [props, set] = useSprings(banners.length, (i) => ({
    x: EDGE_DISTANCE + itemWidth * i
  }));

  const bind = useDrag(
    ({ down, movement: [mx], direction: [xDir], distance }) => {
      let newActive = active;
      if (!down && distance >= itemWidth / 2) {
        const distanceCount = Math.round(distance / itemWidth);
        setActive((active) => {
          newActive = Math.max(
            0,
            Math.min(
              active + (xDir > 0 ? -distanceCount : distanceCount),
              banners.length - 1
            )
          );
          return newActive;
        });
      }

      set((i) => ({
        x: EDGE_DISTANCE + itemWidth * (i - newActive) + (down ? mx : 0),
        immediate: down
      }));
    }
  );

  if (banners.length === 1) {
    const [banner] = banners;
    return (
      <SingleImageContainerStyle
        style={{ width: bannerWidth }}
        aria-label="swiper-panel"
      >
        <ImageContainer
          containerAttr={{
            width: banner.width,
            height: banner.height,
            styles: {
              display: 'inline-block'
            }
          }}
          imageAttr={{
            alt: bannerAltText(),
            src: banner.uri,
            styles: SlideImageStyle
          }}
        />
      </SingleImageContainerStyle>
    );
  }

  return (
    <SliderContainer>
      <StyledSlider {...bind()}>
        {banners.map((banner, index) => (
          <ItemContainer
            key={index}
            style={{
              transform: props[index].x.interpolate(
                (x) => `translate3d(${x}px, 0, 0)`
              ),
              width: bannerWidth
            }}
          >
            <ImageContainer
              containerAttr={{
                width: banner.width,
                height: banner.height,
                styles: ImageContainerStyle
              }}
              imageAttr={{
                alt: bannerAltText(),
                src: banner.uri,
                styles: SlideImageStyle
              }}
            />
          </ItemContainer>
        ))}
      </StyledSlider>
    </SliderContainer>
  );
};

SwiperPanel.propTypes = {
  banners: PropTypes.arrayOf(
    PropTypes.shape({
      uri: PropTypes.string.isRequired
    })
  ).isRequired
};

export default SwiperPanel;
